import _ from 'lodash';
import globalStore from '$$globalStore';
import constants from '$$helpers/constants';
import createWorker from './createWorker';
import projectsModel from '$$projectModel';
import customDayTemplatesModel from '$$customDayTemplatesModel';

class CalendarWorker {
  #worker = null;

  #filterGanttIds = [];

  #dataComposers = {
    getClosestWorkTime: data => {
      const preparedData = { ...data };
      const ganttId = +preparedData.task?.gantt_id || +globalStore.getters['tasksModel/getTask'](preparedData.task?.id)?.gantt_id;

      if (!ganttId) {
        throw 'Gantt id is not provided for calendar worker';
      }

      this.#filterGanttIds.push(ganttId);

      preparedData.ganttId = ganttId;
      preparedData.date = _.isString(preparedData.date) ? new Date(preparedData.date) : preparedData.date;

      return preparedData;
    },

    calculateDurationsForTasks: data => {
      if (!data.ganttId) {
        throw 'Gantt id is not provided for calendar worker';
      }

      this.#filterGanttIds.push(+data.ganttId);

      return data;
    },

    getTasksChangesAfterChangeProjectCalendar: ganttId => {
      if (!ganttId) {
        throw 'Gantt id is not provided for calendar worker';
      }

      const preparedData = {};

      preparedData[ganttId] = globalStore.getters['tasksModel/getItem'](+ganttId).tasks;
      this.#filterGanttIds.push(+ganttId);

      return preparedData;
    },

    getTasksChangesAfterChangeResourceCalendar: resourceId => {
      if (!resourceId) {
        throw 'Resource id is not provided for calendar worker';
      }

      const resourceData = globalStore.getters['resourcesModel/getResourceById'](+resourceId);
      const ganttIds = resourceData.resourceProjects.map(settings => settings.type === constants.RESOURCE_TIME_TYPE && settings.projectId);

      this.#filterGanttIds = ganttIds.filter(ganttId => {
        const projectConfig = projectsModel.getProjectConfig(ganttId);

        if (!projectConfig) {
          return false;
        }

        const isFixedEstimation = +projectConfig.estimation_mode === constants.WORKLOAD_CALCULATION_MODES.fixEstimation;

        return isFixedEstimation && projectConfig.apply_resource_calendar;
      });

      const tasks = globalStore.getters['tasksModel/getTasksByResourceIdAndGanttIds'](+resourceId, this.#filterGanttIds).map(t => t.taskData);
      const preparedData = _.groupBy(tasks, 'gantt_id');

      return preparedData;
    },
  };

  constructor() {
    this.#initWorker();
  }

  #initWorker() {
    let version = Date.now();

    if (GT.productVersion && GT.productVersion.buildVersion) {
      version = GT.productVersion.buildVersion;
    }

    this.#worker = createWorker(`/workers/calendar_worker.js?${version}`);
  }

  #prepareModelsData() {
    const projectsCustomDays = customDayTemplatesModel.serialize();
    const resultData = {
      projectsResources: {},
      resourcesToTasks: {},
      projectConfigs: {},
      commonGanttConfig: {
        start_on_monday: Number(user.settings.start_on_monday),
      },
      constants: {
        RESOURCE_TIME_TYPE: constants.RESOURCE_TIME_TYPE,
      },
    };

    this.#filterGanttIds.forEach(ganttId => {
      const totalData = globalStore.getters['tasksModel/getTotalEstimateDataForProject'](+ganttId);
      const projectConfig = projectsModel.getProjectConfig(+ganttId);
      const customDaysData = projectsCustomDays.find(data => +data.ganttId === +ganttId);

      const workDays = new Array(7).fill(false);

      projectConfig.durationData.showDay.forEach(dayIndex => {
        if (dayIndex === 7) {
          workDays[0] = true; // sunday should be first
        } else {
          workDays[dayIndex] = true;
        }
      });

      resultData.projectsResources[ganttId] = globalStore.getters['resourcesModel/getResourcesByProjectId'](+ganttId) || {};
      resultData.resourcesToTasks[ganttId] = globalStore.getters['tasksModel/getItem'](+ganttId)?.resourcesToTasks || {};
      resultData.projectConfigs[ganttId] = {
        durationData: projectConfig.durationData,
        calendar: {
          id: +ganttId,
          worktime: {
            hours: projectConfig.durationData.showTime,
            days: workDays,
          },
        },
        ganttConfig: {
          auto_scheduling: projectConfig.auto_scheduling,
          estimation_mode: projectConfig.estimation_mode,
          apply_resource_calendar: projectConfig.apply_resource_calendar,
          progress: projectConfig.progress,
          resource_loading_type: projectConfig.resource_loading_type,
        },
        customDays: customDaysData ? customDaysData.customDays : [],
        totalDates: {
          start_date: totalData.start_date,
          end_date: totalData.end_date,
        },
      };
    });

    return resultData;
  }

  async calculate(data, composer, action) {
    this.#filterGanttIds = [];
    const updatedData = this.#dataComposers[composer](data);

    if (!updatedData) {
      return Promise.resolve(false);
    }

    const innerData = this.#prepareModelsData();
    const calculatedGanttData = await this.#worker(_.cloneDeep({
      innerData,
      updatedData,
      action: action || composer,
    }));

    return Promise.resolve(calculatedGanttData);
  }
}

export default new CalendarWorker();
